import type { MigrationConfig } from "drizzle-orm/migrator";
import migrations from "../client-db/src/migrations.json";
import { PGlite } from "@electric-sql/pglite";
import { drizzle } from 'drizzle-orm/pglite';
import { pgDump } from "@electric-sql/pglite-tools";
import { ensureEventsSchemaCompatibility } from "@/db/schema-compat.ts";
import { seedDemoData } from '@/demo/seed-demo-data.ts'
import { seedDemoMedia } from '@/demo/seed-demo-media.ts'
import { isDemoModeEnabled } from '@/demo/demo-mode.ts'

const DB_NAME_KEY = 'TT_CURRENT_DB_NAME'
const DEFAULT_DB_VERSION = 'idb://timethread-v3'
const DEMO_DB_VERSION = 'idb://timethread-demo-v1'

const IS_DEMO_MODE = isDemoModeEnabled()
const ACTIVE_DB_NAME_KEY = IS_DEMO_MODE ? `${DB_NAME_KEY}_DEMO` : DB_NAME_KEY
const CURRENT_DB_VERSION = IS_DEMO_MODE ? DEMO_DB_VERSION : DEFAULT_DB_VERSION

// Initialize database connection
export const client = new PGlite(CURRENT_DB_VERSION);
export let localDB = drizzle(client);

/**
 * Main migration function that handles database version upgrades and applies schema migrations
 */
export async function migrate(): Promise<void> {
  await handleDatabaseVersion();

  // Apply Drizzle migrations to the current database
  // Note: Type casting needed due to Drizzle's internal API structure
  await runDrizzleMigrations();
  await ensureEventsSchemaCompatibility(client);
  if (IS_DEMO_MODE) {
    await seedDemoData(localDB)
    try {
      await seedDemoMedia(localDB)
    } catch (error) {
      console.warn('Demo media seeding failed; continuing without demo photos.', error)
    }
  }
}

export async function runDrizzleMigrations(): Promise<void> {
  await (localDB as any).dialect.migrate(migrations, (localDB as any).session, {
    migrationsTable: "drizzle_migrations",
  } satisfies Omit<MigrationConfig, "migrationsFolder">);
}

/**
 * Checks if database version migration is needed and triggers it if necessary
 */
async function handleDatabaseVersion(): Promise<void> {
  const storedDbVersion = localStorage.getItem(ACTIVE_DB_NAME_KEY);

  if (storedDbVersion && storedDbVersion === CURRENT_DB_VERSION) {
    console.log('Using existing database');
    return;
  }

  // First run for the current mode: just mark active DB version.
  if (!storedDbVersion) {
    localStorage.setItem(ACTIVE_DB_NAME_KEY, CURRENT_DB_VERSION)
    return
  }

  // Database version mismatch: migrate existing data if possible.
  await migratePGLite();
}

/**
 * Resolves and opens the old PGLite database instance for migration
 * @returns Promise resolving to the old PGLite instance
 * @throws Error if unable to determine the old database version
 */
export async function resolveOldPgLite(): Promise<any> {
  const oldDBVersion = localStorage.getItem(ACTIVE_DB_NAME_KEY);

  if (!oldDBVersion) return Promise.resolve(false);

  // Check for legacy database versions that need migration
  if (oldDBVersion.includes('my-pgdata') ||
    oldDBVersion.includes('v2')) {
    const module = await import('pglite-02');
    return new module.PGlite('idb://my-pgdata');
  }

  throw new Error('Unable to determine old PGLite version for migration');
}

/**
 * Migrates data from an old PGLite database to the current version
 * This process involves:
 * 1. Opening the old database
 * 2. Dumping all data and schema
 * 3. Closing the old database
 * 4. Restoring data to the new database
 * 5. Updating the stored database version
 *
 * @param pgOld - Optional old PGLite instance (will be resolved if not provided)
 */
export async function migratePGLite(pgOld?: any): Promise<void> {
  try {
    // Step 1: Open the old database if not provided
    if (!pgOld) {
      pgOld = await resolveOldPgLite();
      if (!pgOld) return;
    }

    // Step 2: Capture current search path and dump all data
    const searchPathResult = await pgOld.query('SHOW SEARCH_PATH;');
    const initialSearchPath = searchPathResult.rows[0].search_path as string;

    const dump = await pgDump({ pg: pgOld });
    const dumpContent = await dump.text();

    // Step 3: Close the old database connection
    await pgOld.close();

    // Step 4: Restore data to the new database
    await client.exec(dumpContent);
    await client.exec(`SET search_path TO ${initialSearchPath};`);

    // Step 5: Update database references and version tracking
    localDB = drizzle(client);
    localStorage.setItem(ACTIVE_DB_NAME_KEY, CURRENT_DB_VERSION);

    console.log('Database migration completed successfully');

  } catch (error) {
    console.error('Database migration failed:', error);
    throw error;
  }
}

/**
 * Deletes a PGLite IndexedDB database
 * @param name - Database name to delete
 * @returns Promise that resolves when deletion is complete
 */
async function deletePGLiteDB(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);

    request.onsuccess = () => resolve();
    request.onerror = (event) => reject((event.target as IDBRequest)?.error);
    request.onblocked = () => {
      console.warn(`Cannot delete database "${name}" - close all tabs using this database first.`);
    };
  });
}
